import { Col, Input, Row, Switch, Table, Tooltip } from "antd";
import moment from "moment";
import { debounce } from "lodash";
import React, { useMemo, useState } from "react";
import { useApi } from "../../../../../hooks/http2";
import { axios, getToken, URL_API } from "../../../../../utils";
import { AlertConfirm, AlertError } from "../../../../util/Alert";
import { Panel, PanelBody, PanelHeader } from "../../../../util/panel";

const CHUNK_STATUS = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
};

export default function KnowledgeChunkList() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const limit = 20;

  const debounceSearch = useMemo(
    () =>
      debounce((value) => {
        setPage(1);
        setSearch(value);
      }, 500),
    []
  );

  const list = useApi(
    {
      baseURL: URL_API,
      url: "/ai/product/knowledge-chunk",
      params: {
        search,
        limit,
        offset: (page - 1) * limit,
      },
      token: true,
    },
    [search, page]
  );

  const handleChangeStatus = async (id, checked) => {
    const nextStatus = checked ? CHUNK_STATUS.ACTIVE : CHUNK_STATUS.INACTIVE;
    const confirm = await AlertConfirm(
      checked ? "เปิดใช้งานข้อมูลนี้" : "ปิดใช้งานข้อมูลนี้"
    );
    if (!confirm) return;

    axios({
      method: "post",
      baseURL: URL_API,
      url: `/ai/product/knowledge-chunk/${id}/status`,
      headers: { Authorization: "Bearer " + getToken() },
      data: { status: nextStatus },
    })
      .then(() => {
        list.reload && list.reload();
      })
      .catch((err) => {
        AlertError({
          text: err && err.response ? err.response.data.message : err.message,
        });
      });
  };

  return (
    <Panel>
      <PanelHeader>ข้อมูล Knowledge Chunk สำหรับ AI</PanelHeader>
      <PanelBody>
        <Row type="flex" justify="space-between" gutter={16} className="mb-4">
          <Col span={8}>
            <Input.Search
              allowClear
              placeholder="ค้นหาด้วยรหัสสินค้า ชื่อสินค้า"
              onChange={(e) => debounceSearch(e.target.value)}
              onSearch={(value) => {
                setPage(1);
                setSearch(value);
              }}
            />
          </Col>
        </Row>
        <Table
          bordered
          size="small"
          loading={list?.loading}
          dataSource={list?.fetch?.list || []}
          rowKey="id"
          pagination={{
            size: "default",
            showLessItems: true,
            showTotal: (total, range) => `${range[0]} - ${range[1]} of ${total}`,
            total: list?.fetch?.total || 0,
            current: page,
            pageSize: limit,
            onChange: (nextPage) => setPage(nextPage),
          }}
        >
          <Table.Column
            title="รหัสสินค้า"
            key="product_code"
            dataIndex="product_code"
            align="center"
          />
          <Table.Column
            title="ชื่อสินค้า"
            key="product_name"
            dataIndex="product_name"
            align="center"
          />
          <Table.Column
            title="เนื้อหา"
            key="content"
            dataIndex="content"
            render={(text) => (
              <Tooltip title={text}>
                <span>
                  {text && text.length > 80 ? `${text.slice(0, 80)}...` : text}
                </span>
              </Tooltip>
            )}
          />
          <Table.Column
            title="แหล่งที่มา"
            key="source"
            dataIndex="source"
            align="center"
          />
          <Table.Column
            title="ภาษา"
            key="lang"
            dataIndex="lang"
            align="center"
          />
          <Table.Column
            title="วันที่สร้าง"
            key="create_date"
            dataIndex="create_date"
            align="center"
            render={(text) => (text ? moment(text).format("DD/MM/YYYY HH:mm") : "")}
          />
          <Table.Column
            title="สถานะ"
            key="status"
            dataIndex="status"
            align="center"
            render={(text, { id }) => (
              <Switch
                checked={text === CHUNK_STATUS.ACTIVE}
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                onChange={(checked) => handleChangeStatus(id, checked)}
              />
            )}
          />
        </Table>
      </PanelBody>
    </Panel>
  );
}
